/**
 * EventHubLayout — chrome shell for the entire /event/* tree.
 *
 * Mirrors IvtLayout: a self-contained product surface with its OWN access gate
 * (see eventHub/utils/authorization), decoupled from the portal Sidebar. The right-side
 * ChatPanel is NOT owned here — it's hoisted into <ChatShell /> (App.tsx) so the
 * same ChatPanel fiber is shared across surfaces.
 *
 * Deliberately thin: an access gate, the portal Sidebar, the impersonation banner, and an
 * <Outlet />. The Sidebar is the main app's own (components/Sidebar) — rendered here because
 * /event is a sibling route of MainAppContent, not inside it (it must stay outside AccessGate,
 * see App.tsx), so it would otherwise have none. Within Event Hub, the landing surface has its
 * own tab strip (EventHubTabs) and every sub-page a breadcrumb.
 *
 * Impersonation follows the portal's View as Other User (see useEventHubLogin). EventHubClient
 * derives the `X-Event-Hub-Impersonate` header per request from the same inputs; this layout
 * warns when Event Hub cannot follow, remounts the pages when the viewed user changes, and
 * provides the roles to render for — the viewed user's, fetched from the service, since the
 * service authorizes as them (see useEventHubRoles). Pages wait for those roles rather than
 * render a moment of the admin's view.
 * Event Hub's own impersonation control (ImpersonateModal, CR-305746938) used to sit here; it
 * was removed, and with this there is one control for the whole portal rather than two that
 * could disagree.
 *
 * Sub-routes mount via <Outlet /> from the parent App.tsx route.
 */

import { Outlet } from 'react-router-dom';
import { Alert } from '@amzn/awsui-components-react';
import { useAuth } from '../../../context/AuthContext';
import ImpersonationBanner from '../../../components/ImpersonationBanner/ImpersonationBanner';
import Sidebar from '../../../components/Sidebar/Sidebar';
import { colorBackgroundLayoutMain } from '@amzn/awsui-design-tokens/polaris';
import { useEventHubLogin } from '../../hooks/useEventHubLogin';
import { EventHubRolesProvider, useEventHubRolesState } from '../../hooks/useEventHubRoles';
import {
  EventHubLoadingOverlay,
  EventHubLoadingProvider,
  useEventHubLoading,
} from './EventHubLoading';
import {
  EVENT_HUB_ACCESS_GROUPS,
  hasEventHubAccess,
} from '../../utils/authorization';
import './EventHubLayout.css';

/** The page background, from the Figma (frame fill #f9f9fa). */
const PAGE_BACKGROUND = '#f9f9fa';

/**
 * AWSUI's ContentLayout paints its own background from this design token, a shade off the
 * page's, so a visible edge showed where it met the padded column around it. Re-pointing the
 * token inside Event Hub makes the two one colour. The custom property's name is read from the
 * token's value (`var(--name, fallback)`) rather than hard-coded, because the name carries a
 * hash that changes between AWSUI versions.
 */
const LAYOUT_MAIN_PROPERTY = /var\((--[^,)]+)/.exec(colorBackgroundLayoutMain)?.[1];
const MAIN_STYLE = {
  background: PAGE_BACKGROUND,
  ...(LAYOUT_MAIN_PROPERTY ? { [LAYOUT_MAIN_PROPERTY]: PAGE_BACKGROUND } : {}),
} as React.CSSProperties;

export default function EventHubLayout() {
  const { uid, ldap_groups } = useAuth();
  /* Only for what this layout renders — the notice and the remount key. The request header is
     NOT set from here: EventHubClient derives it per request from the same inputs (see
     eventHubImpersonation), which keeps render pure and leaves no ordering to get right. */
  const { login, unsupported } = useEventHubLogin();

  // "Holds any Event Hub group", not "holds `event-hub`" — see hasEventHubAccess.
  // The service admits any caller with a non-empty role set, so a user granted only
  // `event-hub-bd` must not be denied here.
  if (!hasEventHubAccess(ldap_groups)) {
    return (
      <div className="event-hub-layout__denied">
        <h2>Event Hub Access Denied</h2>
        <p>
          Account <strong>{uid}</strong> is not in any Event Hub LDAP group (
          {EVENT_HUB_ACCESS_GROUPS.map((g, i) => (
            <span key={g}>
              {i > 0 && ', '}
              <code>{g}</code>
            </span>
          ))}
          ), so the module is unavailable.
        </p>
        <p>Please contact your administrator to request access.</p>
      </div>
    );
  }

  return (
    <EventHubLoadingProvider>
      {(loading) => (
        /* The frame holds the scrolling column and the overlay side by side, so the mask
           stays put over the visible area however far the page has scrolled. */
        <div className="event-hub-layout__frame">
          {/* Fixed to the left edge (Sidebar.css); the frame leaves room for it. */}
          <Sidebar />
          <main className="event-hub-layout__main" aria-busy={loading} style={MAIN_STYLE}>
            <ImpersonationBanner />
            {/* The portal banner above says "viewing as X"; without this, Event Hub would show
                the admin's own data under it and nothing would say so. */}
            {unsupported ? (
              <div className="event-hub-layout__notice">
                <Alert type="warning" header="Event Hub is showing your own data">
                  Viewing as another user in Event Hub requires the <code>event-hub-admin</code>{' '}
                  group. You are in the portal&apos;s admin group but not Event Hub&apos;s, so the
                  pages below are yours, not theirs.
                </Alert>
              </div>
            ) : null}
            <div className="event-hub-layout__content">
              <EventHubRolesProvider>
                {/* Keyed by the login so switching who is being viewed remounts every page.
                    Each page fetches once on mount; without the remount it would keep showing
                    the previous user's data under the new user's banner. */}
                <RolesGate key={login} />
              </EventHubRolesProvider>
            </div>
          </main>
          <EventHubLoadingOverlay visible={loading} />
        </div>
      )}
    </EventHubLoadingProvider>
  );
}

/**
 * Holds the pages back until the roles to render for are known. Only ever waits while acting as
 * someone; otherwise the roles come from the token and are known at once.
 */
function RolesGate() {
  const { loading, error } = useEventHubRolesState();
  useEventHubLoading(loading);
  if (loading) {
    // The overlay shows meanwhile; the pages wait, so the admin's view never flashes.
    return null;
  }
  if (error) {
    /* Not a fallback to the admin's roles: that would show buttons the service will refuse
       for this user. Nothing is better than the wrong thing here. */
    return (
      <div className="event-hub-layout__notice">
        <Alert type="error" header="Could not load the viewed user's permissions">
          {error}
        </Alert>
      </div>
    );
  }
  return <Outlet />;
}
